/**
 * Geometry helpers for the cartographic UI.
 *
 * Why this exists: OpenStreetMap has no name for about four in five Spanish dog parks, so
 * most park cards would otherwise read "Zona canina", "Zona canina", "Zona canina"... In
 * Sevilla, 23 of 25 parks are unnamed and 20 of 25 have no recorded attributes at all. The
 * only fact we hold for every single park, without exception, is its position.
 *
 * So position is what we use to tell them apart. Everything here is derived from
 * coordinates we already have: no API calls, no cost, works on all 2,215 parks.
 */

/** Bounding box of a set of parks, with a little breathing room. */
export function cityBounds(parks) {
  const lats = parks.map((p) => p.lat)
  const lngs = parks.map((p) => p.lng)
  let minLat = Math.min(...lats)
  let maxLat = Math.max(...lats)
  let minLng = Math.min(...lngs)
  let maxLng = Math.max(...lngs)

  // A city with one park has a zero-size box; give it a real one.
  const padLat = Math.max((maxLat - minLat) * 0.12, 0.004)
  const padLng = Math.max((maxLng - minLng) * 0.12, 0.005)
  minLat -= padLat
  maxLat += padLat
  minLng -= padLng
  maxLng += padLng

  return { minLat, maxLat, minLng, maxLng }
}

/** Mean position of the parks — used as "the centre" for describing where a park sits. */
export function cityCentre(parks) {
  const n = parks.length || 1
  return {
    lat: parks.reduce((s, p) => s + p.lat, 0) / n,
    lng: parks.reduce((s, p) => s + p.lng, 0) / n,
  }
}

/** Park position as a 0..1 fraction inside the city box, ready to plot in an SVG. */
export function toUnit(park, bounds) {
  const { minLat, maxLat, minLng, maxLng } = bounds
  return {
    x: (park.lng - minLng) / (maxLng - minLng || 1),
    // SVG y grows downward; latitude grows upward.
    y: 1 - (park.lat - minLat) / (maxLat - minLat || 1),
  }
}

const R = 6371 // km

export function distanceKm(a, b) {
  const dLat = ((b.lat - a.lat) * Math.PI) / 180
  const dLng = ((b.lng - a.lng) * Math.PI) / 180
  const la1 = (a.lat * Math.PI) / 180
  const la2 = (b.lat * Math.PI) / 180
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(la1) * Math.cos(la2) * Math.sin(dLng / 2) ** 2
  return 2 * R * Math.asin(Math.sqrt(h))
}

const SECTORS = [
  'norte', 'noreste', 'este', 'sureste',
  'sur', 'suroeste', 'oeste', 'noroeste',
]

/**
 * Which part of the town a park is in, as a compass sector from the centre.
 * Derived, not guessed: it is a bearing computed from two real coordinates.
 */
export function sector(park, centre) {
  const dLng = (park.lng - centre.lng) * Math.cos((centre.lat * Math.PI) / 180)
  const dLat = park.lat - centre.lat
  // Bearing clockwise from north.
  let deg = (Math.atan2(dLng, dLat) * 180) / Math.PI
  if (deg < 0) deg += 360
  return SECTORS[Math.round(deg / 45) % 8]
}

/**
 * The human label under a park's title: where it is, and how far from the middle of town.
 * "noreste · 2,4 km del centro"
 *
 * Parks very close to the centre are not given a direction, because "0.2 km northeast of
 * the centre" is noise dressed up as information.
 */
export function placeLabel(park, centre) {
  const d = distanceKm(park, centre)
  const km = d.toFixed(1).replace('.', ',')
  if (d < 0.6) return 'en el centro'
  return `${sector(park, centre)} · a ${km} km del centro`
}

/** Spanish-formatted coordinates, for the mono readout. */
export function coordLabel(park) {
  const f = (n) => n.toFixed(4).replace('.', ',')
  return `${f(park.lat)}, ${f(park.lng)}`
}
